import { messaging, VAPID_KEY, getToken, onMessage } from '../config/firebase';
import { usersAPI } from '../api/api';

class PushNotificationService {
    constructor() {
        this.isInitialized = false;
        this.currentToken = null;
        this.subscriptionId = null;
    }

    /**
     * Initialize push notification service
     */
    async init() {
        if (!messaging) {
            console.warn('[PushNotificationService] Firebase Messaging not available');
            return false;
        }

        if (this.isInitialized) {

            return true;
        }

        try {
            // Register Service Worker
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');


            // Listen for foreground messages
            onMessage(messaging, (payload) => {

                this.showNotification(payload);
            });

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('[PushNotificationService] Initialization error:', error);
            return false;
        }
    }

    /**
     * Request notification permission
     */
    async requestPermission() {
        if (!('Notification' in window)) {
            console.warn('[PushNotificationService] This browser does not support notifications');
            return 'denied';
        }

        try {
            const permission = await Notification.requestPermission();

            return permission;
        } catch (error) {
            console.error('[PushNotificationService] Permission request error:', error);
            return 'denied';
        }
    }

    /**
     * Get FCM token
     */
    async getToken() {
        if (!messaging) {
            throw new Error('Firebase Messaging not available');
        }

        try {
            const token = await getToken(messaging, { vapidKey: VAPID_KEY });

            this.currentToken = token;
            return token;
        } catch (error) {
            console.error('[PushNotificationService] Error getting token:', error);
            throw error;
        }
    }

    /**
     * Subscribe to push notifications
     */
    async subscribe() {
        try {
            const permission = await this.requestPermission();
            
            if (permission !== 'granted') {
                throw new Error('Notification permission denied');
            }

            const token = await this.getToken();

            // Send token to backend
            const response = await usersAPI.subscribeToPush(token);
            
            if (response.success) {
                this.subscriptionId = response.subscription_id;
                localStorage.setItem('push_subscription_id', response.subscription_id);
                localStorage.setItem('push_fcm_token', token);

                return response;
            } else {
                throw new Error(response.message || 'Subscription failed');
            }
        } catch (error) {
            console.error('[PushNotificationService] Subscription error:', error);
            throw error;
        }
    }

    /**
     * Unsubscribe from push notifications
     */
    async unsubscribe() {
        try {
            const response = await usersAPI.unsubscribeFromPush();
            
            if (response.success) {
                this.currentToken = null;
                this.subscriptionId = null;
                localStorage.removeItem('push_subscription_id');
                localStorage.removeItem('push_fcm_token');

                return response;
            } else {
                throw new Error(response.message || 'Unsubscribe failed');
            }
        } catch (error) {
            console.error('[PushNotificationService] Unsubscribe error:', error);
            throw error;
        }
    }

    /**
     * Send test notification
     */
    async sendTestNotification() {
        try {
            const response = await usersAPI.sendTestPushNotification();
            return response;
        } catch (error) {
            console.error('[PushNotificationService] Test notification error:', error);
            throw error;
        }
    }

    /**
     * Check if user is subscribed
     */
    isSubscribed() {
        return !!localStorage.getItem('push_subscription_id');
    }

    /**
     * Get current permission status
     */
    getPermissionStatus() {
        if (!('Notification' in window)) {
            return 'denied';
        }
        return Notification.permission;
    }

    /**
     * Show notification (for foreground messages)
     */
    showNotification(payload) {
        const title = payload.notification?.title || payload.data?.title || 'InterStrip Értesítés';
        const options = {
            body: payload.notification?.body || payload.data?.body || '',
            icon: payload.notification?.icon || payload.data?.icon || '/images/logo_sidebar_closed.png',
            badge: '/images/logo_sidebar_closed.png',
            tag: payload.data?.tag || 'interstrip-notification',
            data: payload.data || {},
        };

        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(title, options);
        }
    }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
